import { GridFilterItem } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buildColumnDefs,
  buildFilterModel,
  buildInitialColumnSelection,
  buildShipmentTypeOptions,
  buildStatusOptions,
  EMPTY_FILTERS,
  LabelValue,
} from './shipmentListFilters';
import { ShipmentListResponse } from '../../../@types/shipment.type';
import type { FilterState } from '../../../components/molecules/FilterBar';
import { ColumnDef, reorderByVisibleIndex } from '../../../components/organisms/ShipmentTable';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { useColumnRefetchScheduler } from '../../../hooks/useColumnRefetchScheduler';
import { useRefetchLoopGuard } from '../../../hooks/useRefetchLoopGuard';
import {
  useGetAllShipments,
  useGetAllShipmentStatus,
  useGetAllShipmentTypes,
} from '../../../queries/shipment-query';
import { getVisibleFields } from '../../../utils/columnSelectionOperations';

export interface SortState {
  field: string;
  sort: 'asc' | 'desc' | null | undefined;
}

export interface ShipmentListDataReturn {
  data: Array<Record<string, unknown>>;
  isLoading: boolean;
  isFetching: boolean;
  page: number;
  pageSize: number;
  total: number;
  fetchDurationMs: number | null;
  sortModel: SortState;
  columns: ColumnDef[];
  columnSelectionData: ColumnSelectionItem[];
  filterModel: { items: GridFilterItem[] };
  statusOptions: LabelValue[];
  typeOptions: LabelValue[];
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSortModel: (model: SortState) => void;
  applyFilters: (filters: FilterState) => void;
  updateColumnSelection: (data: ColumnSelectionItem[]) => void;
  reorderColumns: (oldIndex: number, newIndex: number) => void;
  refetch: () => void;
}

export const useShipmentListData = (): ShipmentListDataReturn => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortModel, setSortModel] = useState<SortState>({ field: 'id', sort: 'desc' });
  const [filterModel, setFilterModel] = useState<{ items: GridFilterItem[] }>(() =>
    buildFilterModel(EMPTY_FILTERS)
  );
  const [columns, setColumns] = useState<ColumnDef[]>(() => buildColumnDefs());
  const [colSelectionData, setColSelectionData] = useState<ColumnSelectionItem[]>(() =>
    buildInitialColumnSelection()
  );
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const fetchStartRef = useRef<number | null>(null);
  const prevDataRef = useRef<Array<Record<string, unknown>>>([]);
  const prevTotalRef = useRef<number>(0);

  const statusQuery = useGetAllShipmentStatus();

  const statusOptions = useMemo<LabelValue[]>(
    () => buildStatusOptions(statusQuery.data),
    [statusQuery.data]
  );

  const typeQuery = useGetAllShipmentTypes();
  const typeOptions = useMemo<LabelValue[]>(
    () => buildShipmentTypeOptions(typeQuery.data),
    [typeQuery.data]
  );

  const queryOption = useMemo(
    () => ({
      filterModel,
      page,
      pageSize,
      sortModel: sortModel.sort ? [{ field: sortModel.field, sort: sortModel.sort }] : [],
    }),
    [filterModel, page, pageSize, sortModel]
  );

  const query = useGetAllShipments(queryOption, colSelectionData);

  // All manual refetch paths share one loop guard — see useRefetchLoopGuard.
  // The fetch timer starts inside the guarded callback so a call the guard
  // suppresses cannot record a stale start time.
  const guardedRefetch = useRefetchLoopGuard(
    useCallback(() => {
      fetchStartRef.current = performance.now();
      query.refetch();
    }, [query]),
    'Shipments list (/shipmentsList)'
  );

  const { schedule: scheduleColumnRefetch, cancel: cancelColumnRefetch } =
    useColumnRefetchScheduler(guardedRefetch);

  // A pending column-triggered refetch would only duplicate the full
  // refetch about to run with the latest options — drop it.
  useEffect(() => {
    cancelColumnRefetch();
    guardedRefetch();
  }, [queryOption, cancelColumnRefetch, guardedRefetch]);

  useEffect(() => {
    if (!query.isFetching && fetchStartRef.current !== null) {
      setFetchDurationMs(performance.now() - fetchStartRef.current);
      fetchStartRef.current = null;
    }
  }, [query.isFetching]);

  const applyFilters = useCallback((filters: FilterState) => {
    setFilterModel(buildFilterModel(filters));
    setPage(0);
  }, []);

  const updateColumnSelection = useCallback(
    (data: ColumnSelectionItem[]) => {
      const prevVisible = getVisibleFields(colSelectionData);
      const nextVisible = getVisibleFields(data);
      const hasAddedField = Array.from(nextVisible).some((f) => !prevVisible.has(f));

      setColSelectionData(data);
      setColumns((prev) => prev.map((c) => ({ ...c, visible: nextVisible.has(c.key) })));

      if (hasAddedField) scheduleColumnRefetch();
    },
    [colSelectionData, scheduleColumnRefetch]
  );

  const reorderColumns = useCallback((oldIndex: number, newIndex: number) => {
    setColumns((prev) => reorderByVisibleIndex(prev, oldIndex, newIndex));
  }, []);

  // Keep `prevDataRef` in sync so we can render the last-seen page while the
  // next page is loading, avoiding a content flash.
  useEffect(() => {
    const resp = query.data as ShipmentListResponse | undefined;
    if (resp?.data) {
      prevDataRef.current = resp.data as unknown as Array<Record<string, unknown>>;
      prevTotalRef.current = resp.total ?? 0;
    }
  }, [query.data]);

  const resp = query.data as ShipmentListResponse | undefined;
  const currentData =
    (resp?.data as unknown as Array<Record<string, unknown>> | undefined) ?? prevDataRef.current;
  const currentTotal = resp?.total ?? prevTotalRef.current;

  return {
    data: currentData,
    isLoading: query.isLoading && prevDataRef.current.length === 0,
    isFetching: query.isFetching,
    page,
    pageSize,
    total: currentTotal,
    fetchDurationMs,
    sortModel,
    columns,
    columnSelectionData: colSelectionData,
    filterModel,
    statusOptions,
    typeOptions,
    setPage,
    setPageSize,
    setSortModel,
    applyFilters,
    updateColumnSelection,
    reorderColumns,
    refetch: guardedRefetch,
  };
};
